// NotificationContext — shared feed for the topbar bell + sidebar nav badges.
// Polls the user-scoped /notifications endpoint and exposes unread counts by module.
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [byModule, setByModule] = useState({});
    const timer = useRef(null);

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications");
            setItems(data.items || []);
            setUnreadTotal(data.unread_total || 0);
            setByModule(data.by_module || {});
        } catch { /* stay quiet — bell just shows nothing */ }
    }, [user]);

    useEffect(() => {
        if (!user) { setItems([]); setUnreadTotal(0); setByModule({}); return; }
        refresh();
        timer.current = setInterval(refresh, 45000);
        return () => timer.current && clearInterval(timer.current);
    }, [user, refresh]);

    const markRead = useCallback(async (id) => {
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        try { await api.post(`/notifications/${id}/read`); } catch { /* noop */ }
        refresh();
    }, [refresh]);

    const markAllRead = useCallback(async () => {
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadTotal(0); setByModule({});
        try { await api.post("/notifications/read-all"); } catch { /* noop */ }
        refresh();
    }, [refresh]);

    const markModuleRead = useCallback(async (module) => {
        if (!module || !byModule[module]) return;
        setByModule((prev) => { const n = { ...prev }; delete n[module]; return n; });
        setUnreadTotal((t) => Math.max(0, t - (byModule[module] || 0)));
        try { await api.post(`/notifications/module/${module}/read`); } catch { /* noop */ }
        refresh();
    }, [byModule, refresh]);

    return (
        <NotificationContext.Provider value={{ items, unreadTotal, byModule, refresh, markRead, markAllRead, markModuleRead }}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotificationContext);
    return ctx || { items: [], unreadTotal: 0, byModule: {}, refresh: () => {}, markRead: () => {}, markAllRead: () => {}, markModuleRead: () => {} };
}
